import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { START_LEAVE_DIALOG } from "../../../store/slices/utils/dialogEnum";
import { closeDialog } from "../../../store/slices/dialog/dialogSlice";
import Modal from "@mui/material/Modal";
import { useFormik } from "formik";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import FormControl from "@mui/material/FormControl";
import CustomDatePicker from "../../../components/shared/CustomDatePicker";
import FormHelperText from "@mui/material/FormHelperText";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { setEnterDateModel } from "../../../store/slices/user/userSlice";
import * as yup from "yup";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import {
  openErrorToast,
  openSuccessToast,
} from "../../../store/slices/toast/toastSlice";
import dayjs from "dayjs";
import { useEnterDateMutation } from "../../../store";

const StartLeaveForm = () => {
  const dispatch = useDispatch();
  const [disableSubmitButton, setDisableSubmitButton] = React.useState(false);
  const { enterDateModel } = useSelector((state) => state.userSlice);
  const [enterDate, { isLoading }] = useEnterDateMutation();
  const handleClose = () => {
    dispatch(setEnterDateModel({ userId: null, isStartDate: true }));
    dispatch(closeDialog(START_LEAVE_DIALOG));
  };
  const { startLeaveDialogOpen } = useSelector((state) => state.dialogSlice);
  const handleChangeDate = ({ name, value }) => {
    const day = new Date(value).getDay();
    if (day === 6 || day === 0) {
      setDisableSubmitButton(true);
      dispatch(openErrorToast("Hafta İçi Seçim Yapınız !"));
      formik.setFieldValue(name, dayjs(null).format("YYYY-MM-DD"));
    } else {
      setDisableSubmitButton(false);
      formik.setFieldValue(name, value);
    }
  };
  const formik = useFormik({
    initialValues: {
      userId: enterDateModel.userId,
      isStartDate: enterDateModel.isStartDate,
      date: "",
    },
    validationSchema: yup.object().shape({
      date: yup.date().required("Bu alan zorunludur"),
    }),
    onSubmit: async (values, bag) => {
      try {
        const model = {
          userId: values.userId,
          isStartDate: values.isStartDate,
          date:
            values.date !== null
              ? dayjs(values.date).format("YYYY-MM-DD")
              : null,
        };
        enterDate(model).then((res) => {
          if (res) {
            if (!res.data.isSuccess) {
              dispatch(openErrorToast(res.data.message));
            } else {
              dispatch(openSuccessToast("İşlem Başarılı"));
              handleClose();
            }
          }
        });
      } catch (error) {
        bag.setErrors({ general: error.response.data.message });
      }
    },
  });

  return (
    <Modal
      open={startLeaveDialogOpen}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "92vw", sm: 440 },
          bgcolor: "#fff",
          borderRadius: 4,
          boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
          overflow: "hidden",
          outline: "none",
        }}
      >
        {/* Üst Banner */}
        <Box
          sx={{
            background: enterDateModel.isStartDate
              ? "linear-gradient(135deg, #1a6b3a 0%, #0f4525 100%)"
              : "linear-gradient(135deg, #8B1A1A 0%, #5c0a0a 100%)",
            px: 3,
            py: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <CalendarMonthIcon sx={{ color: "#fff", fontSize: 28 }} />
          <Typography
            id="modal-modal-title"
            variant="h6"
            sx={{ fontWeight: 700, color: "#fff", letterSpacing: 0.3 }}
          >
            {enterDateModel.isStartDate ? "Başlayış Tarihi Gir" : "Ayrılış Tarihi Gir"}
          </Typography>
        </Box>

        {/* Personel Bilgi Kartı */}
        {enterDateModel.personelName && (
          <Box
            sx={{
              mx: 3,
              mt: 3,
              mb: 1,
              p: 2,
              borderRadius: 3,
              bgcolor: enterDateModel.isStartDate ? "#f0fdf4" : "#fef2f2",
              border: `1px solid ${enterDateModel.isStartDate ? "#86efac" : "#fca5a5"}`,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            {/* Avatar */}
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                bgcolor: enterDateModel.isStartDate ? "#16a34a" : "#8B1A1A",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>
                {enterDateModel.personelName
                  .split(" ")
                  .map((n) => n[0])
                  .slice(0, 2)
                  .join("")
                  .toUpperCase()}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#1e293b" }}>
                {enterDateModel.personelName}
              </Typography>
              {enterDateModel.departmentName && (
                <Typography variant="caption" sx={{ color: "#64748b" }}>
                  {enterDateModel.departmentName}
                </Typography>
              )}
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.3,
                  fontWeight: 600,
                  color: enterDateModel.isStartDate ? "#16a34a" : "#8B1A1A",
                }}
              >
                {enterDateModel.isStartDate ? "İşe başlayış yapılıyor" : "Şirketten ayrılış yapılıyor"}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Form Alanı */}
        <Box sx={{ px: 3, pb: 3, mt: 2 }} id="modal-modal-description">
          {formik.errors.general && (
            <Box textAlign="center" sx={{ mb: 2 }}>
              <Alert severity="error">{formik.errors.general}</Alert>
            </Box>
          )}
          <form onSubmit={formik.handleSubmit}>
            <FormControl
              fullWidth
              sx={{ mb: 2 }}
              error={formik.touched.date && Boolean(formik.errors.date)}
            >
              <CustomDatePicker
                handleChange={handleChangeDate}
                value={formik.values.date}
                id="date"
                label={enterDateModel.isStartDate ? "Başlayış Tarihi" : "Ayrılış Tarihi"}
              />
              {formik.touched.date && (
                <FormHelperText>{formik.errors.date}</FormHelperText>
              )}
            </FormControl>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                type="button"
                onClick={handleClose}
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                sx={{ borderRadius: 2, fontWeight: 600 }}
              >
                Vazgeç
              </Button>
              <Button
                loading={isLoading}
                variant="contained"
                loadingPosition="end"
                disabled={disableSubmitButton}
                type="submit"
                color={enterDateModel.isStartDate ? "success" : "error"}
                startIcon={<SaveIcon />}
                sx={{ borderRadius: 2, fontWeight: 600 }}
              >
                Kaydet
              </Button>
            </Stack>
          </form>
        </Box>
      </Box>
    </Modal>
  );
};

export default StartLeaveForm;
